import React from "react";
import { motion, AnimatePresence } from "framer-motion";

// Mobile-first download popup. Uses Tailwind classes present in the project.
const DownloadAppPopup = ({ onClose }) => {
  // Detect iOS device
  const isIOS =
    /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -100, opacity: 0 }}
        transition={{ type: "spring", duration: 0.5, bounce: 0 }}
        className="pointer-events-none fixed left-0 right-0 top-0 z-[99] px-4 pb-2 pt-4"
      >
        {/* Popup Container */}
        <div className="pointer-events-auto mx-auto max-w-4xl">
          <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-gray-800 dark:bg-[#1a1a1a]">
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute right-3 top-3 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-gray-100 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700"
              aria-label="Close"
            >
              <svg
                className="h-4 w-4 text-gray-600 dark:text-gray-300"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            {/* Content - Horizontal Layout */}
            <div className="flex items-center gap-4 p-4 pr-12">
              {/* App Icon */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{
                  delay: 0.2,
                  type: "spring",
                  stiffness: 200,
                  damping: 15,
                }}
                className="flex-shrink-0"
              >
                <div className="relative">
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[#36454F] to-[#000000] opacity-40 blur-lg"></div>
                  <img
                    src="/images/logo/logo-main.png"
                    alt="Muscat Where To"
                    className="relative h-14 w-14 rounded-2xl dark:hidden sm:h-16 sm:w-16"
                  />
                  <img
                    src="/images/logo/logo-main-light.png"
                    alt="Muscat Where To"
                    className="relative hidden h-14 w-14 rounded-2xl dark:block sm:h-16 sm:w-16"
                  />
                </div>
              </motion.div>

              {/* Text Content */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                className="min-w-0 flex-1"
              >
                <h3 className="mb-0.5 text-sm font-bold text-gray-900 dark:text-white sm:text-base">
                  It's better in the APP
                </h3>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Download it now
                </p>
              </motion.div>

              {/* Download Button */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4 }}
                className="flex-shrink-0"
              >
                {isIOS ? (
                  <a
                    href="https://apps.apple.com/in/app/muscat-where-to/id6444629353"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative inline-flex transform items-center gap-2 whitespace-nowrap rounded-xl bg-[#000000] px-4 py-2.5 text-sm font-semibold text-white transition-all shadow-lg hover:scale-105 hover:bg-[#36454F] hover:shadow-xl active:scale-95 sm:px-5"
                  >
                    <svg
                      className="h-4 w-4 sm:h-5 sm:w-5"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.09997 22C7.78997 22.05 6.79997 20.68 5.95997 19.47C4.24997 17 2.93997 12.45 4.69997 9.39C5.56997 7.87 7.12997 6.91 8.81997 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z" />
                    </svg>
                    <span className="hidden xs:inline">Download</span>
                    <span className="xs:hidden">GET</span>
                  </a>
                ) : (
                  <a
                    href="https://play.google.com/store/apps/details?id=com.muscatwhereto.android"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative inline-flex transform items-center gap-2 whitespace-nowrap rounded-xl bg-[#000000] px-4 py-2.5 text-sm font-semibold text-white transition-all shadow-lg hover:scale-105 hover:bg-[#36454F] hover:shadow-xl active:scale-95 sm:px-5"
                  >
                    <svg
                      className="h-4 w-4 sm:h-5 sm:w-5"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                    </svg>
                    <span className="hidden xs:inline">Download</span>
                    <span className="xs:hidden">GET</span>
                  </a>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default DownloadAppPopup;
